import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  Flex,
  Icon,
  Skeleton,
  Text,
} from '@kognitos/lattice';
import { parseResourceName, showToast } from '@/shared/utils';
import {
  useUserPreferencesStore,
  selectCurrentOrganizationId,
  selectSetCurrentOrganizationId,
  selectSetCurrentWorkspaceId,
  useOrganizationSwitcherStore,
} from '@/shared/stores';
import { getRoutePath, ONBOARDING_STEPS } from '@/routes/util';
import { useCurrentUserOrganizations } from '@/generated/hooks/agora/v1/organization';
import { getCurrentUserOrganizationWorkspacesQueryOptions } from '@/generated/hooks/agora/v1/workspace';

const OrganizationSwitcher = () => {
  const isOpen = useOrganizationSwitcherStore(state => state.isOpen);
  const openOrgSwitcher = useOrganizationSwitcherStore(state => state.open);
  const closeOrgSwitcher = useOrganizationSwitcherStore(state => state.close);

  const [searchQuery, setSearchQuery] = useState('');
  const { t: tOrg } = useTranslation('organization');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const currentOrganizationId = useUserPreferencesStore(
    selectCurrentOrganizationId,
  );
  const setCurrentOrganizationId = useUserPreferencesStore(
    selectSetCurrentOrganizationId,
  );
  const setCurrentWorkspaceId = useUserPreferencesStore(
    selectSetCurrentWorkspaceId,
  );

  const {
    data: organizationsData,
    isLoading: isOrganizationsLoading,
    isError: isOrganizationsError,
  } = useCurrentUserOrganizations();

  // Pre-compute user organizations
  const userOrgs = useMemo(
    () =>
      organizationsData?.data.user_organizations?.map(org => ({
        ...org,
        name: parseResourceName(org.name!).organization,
      })) || [],
    [organizationsData],
  );

  // Filter organizations by search query (title only)
  const filteredOrganizations = useMemo(() => {
    if (!searchQuery.trim()) {
      return userOrgs;
    }
    return userOrgs.filter(org =>
      org.title.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [userOrgs, searchQuery]);

  // Handle organization select
  const handleOrganizationSelect = async (orgId: string) => {
    if (orgId === currentOrganizationId) {
      closeOrgSwitcher();
      return;
    }

    try {
      // This fetch is user-action driven (onSelect), so we use fetchQuery here
      // instead of a render-time hook subscription.
      const orgWorkspaces = await queryClient.fetchQuery(
        getCurrentUserOrganizationWorkspacesQueryOptions(orgId),
      );
      const firstWorkspace = orgWorkspaces.data.user_workspaces?.[0];
      const newWorkspaceId = firstWorkspace?.name
        ? parseResourceName(firstWorkspace.name).workspace
        : '';

      // Preserve the target org while the previous route still syncs preferences.
      if (!newWorkspaceId) {
        setCurrentOrganizationId(orgId);
        setCurrentWorkspaceId('');
        navigate(
          getRoutePath('ONBOARDING', {
            onboardingStep: ONBOARDING_STEPS.WORKSPACE,
            hasExistingOrg: 'true',
            orgId,
          }),
        );
        closeOrgSwitcher();
        return;
      }

      // Navigate to the workspace root so ProtectedRoute picks the landing
      // page (finance cockpit vs. automations/drafts) for the target workspace.
      navigate(
        getRoutePath('WORKSPACE', {
          orgId,
          workspaceId: newWorkspaceId,
        }),
      );
      closeOrgSwitcher();
    } catch {
      closeOrgSwitcher();
      showToast(
        tOrg('organization.switch.error.switchFailed'),
        undefined,
        'error',
      );
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (open) {
      openOrgSwitcher();
    } else {
      closeOrgSwitcher();
      setSearchQuery('');
    }
  };

  const renderLoadingState = () => {
    return (
      <>
        {[0, 1, 2].map(item => (
          <CommandItem key={item}>
            <Skeleton className="h-4 w-36" />
          </CommandItem>
        ))}
      </>
    );
  };

  const renderErrorState = () => {
    return (
      <Flex paddingY={8} paddingX={2} direction="column" gap={2} align="center">
        <Text level="small" color="destructive" weight="normal">
          {tOrg('organization.switch.error.title')}
        </Text>
        <Text level="xSmall" color="muted" weight="normal">
          {tOrg('organization.switch.error.description')}
        </Text>
      </Flex>
    );
  };

  return (
    <CommandDialog open={isOpen} onOpenChange={handleOpenChange}>
      <CommandInput
        placeholder={tOrg('organization.switch.placeholder')}
        value={searchQuery}
        onValueChange={setSearchQuery}
      />
      <CommandList>
        <CommandEmpty>
          {tOrg('organization.switch.noOrganizations')}
        </CommandEmpty>
        <CommandGroup heading={tOrg('organization.switch.title')}>
          {isOrganizationsLoading
            ? renderLoadingState()
            : isOrganizationsError
              ? renderErrorState()
              : filteredOrganizations.map(org => (
                  <CommandItem
                    key={org.name}
                    value={`${org.title} ${org.name}`}
                    className="cursor-pointer"
                    onSelect={() => {
                      if (org.name) {
                        void handleOrganizationSelect(org.name);
                      }
                    }}
                  >
                    <span className="flex-1">{org.title}</span>
                    {org.name === currentOrganizationId ? (
                      <Icon
                        type="Check"
                        size="sm"
                        className="text-foreground"
                      />
                    ) : null}
                  </CommandItem>
                ))}
        </CommandGroup>

        {/* Only show create new organization button when not filtering/searching */}
        {!searchQuery.trim() && (
          <>
            <CommandSeparator className="mb-2" />
            <CommandGroup className="p-2">
              <CommandItem
                className="cursor-pointer"
                onSelect={() => {
                  closeOrgSwitcher();
                  navigate(
                    getRoutePath('ONBOARDING', {
                      onboardingStep: ONBOARDING_STEPS.ORGANIZATION,
                      hasExistingOrg: 'true',
                    }),
                  );
                }}
              >
                <Icon type="Plus" size="sm" className="text-foreground" />
                {tOrg('organization.switch.createNewOrganization')}
              </CommandItem>
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
};

export default OrganizationSwitcher;
